import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Check } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { getUtm } from "../lib/utm";
import { Reveal } from "./Reveal";

export default function EmailCapture({ source = "landing_page", releaseId = null }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    const utm = getUtm();
    try {
      await api.post("/fans", {
        email,
        signupSource: source,
        releaseId,
        utmSource: utm.utm_source || null,
        utmCampaign: utm.utm_campaign || null,
      });
      track("email_signup", { releaseId, metadata: { source } });
      setStatus("done");
      setMsg("You're on the list. ¡Bienvenido!");
      setEmail("");
      setTimeout(() => navigate("/thanks"), 600);
    } catch (err) {
      setStatus("error");
      setMsg(err?.response?.data?.detail || "Something went wrong. Try again.");
    }
  };

  return (
    <section id="join" data-testid="email-capture" className="px-6 py-20 sm:px-10 sm:py-28 lg:px-16">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-gold/20 bg-surface/60 p-8 sm:p-12">
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gold/10 blur-3xl" />
            <div className="relative">
              <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-gold/15 text-gold">
                <Mail size={22} />
              </div>
              <h2 className="font-display text-3xl font-bold uppercase leading-none tracking-tight text-white sm:text-5xl">
                Join the Código List
              </h2>
              <p className="mt-4 max-w-md text-base text-white/60">
                Get new music, videos, private previews and early drops before everyone else.
              </p>

              {status === "done" ? (
                <div
                  data-testid="email-success"
                  className="mt-8 flex items-center gap-3 rounded-2xl border border-gold/30 bg-gold/10 px-5 py-4 text-gold"
                >
                  <Check size={20} />
                  <span className="font-semibold">{msg}</span>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <input
                    data-testid="email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="h-14 flex-1 rounded-full border border-white/15 bg-ink/60 px-6 text-base text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold"
                  />
                  <button
                    data-testid="email-submit"
                    type="submit"
                    disabled={status === "loading"}
                    className="h-14 rounded-full bg-gold px-8 text-sm font-bold uppercase tracking-wide text-black transition-transform active:scale-95 hover:bg-gold-2 disabled:opacity-60"
                  >
                    {status === "loading" ? "Joining…" : "Join"}
                  </button>
                </form>
              )}
              {status === "error" && (
                <p data-testid="email-error" className="mt-3 text-sm text-red-400">{msg}</p>
              )}
              <p className="mt-4 text-xs text-white/30">
                We only email about new music. No spam. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
